import { html, type TemplateResult } from 'lit-html'
import { GAME_CONFIG, generateIsland, seedFromHex, api as coreApi } from '@guild/core'

import type { App } from '../app.ts'
import { communityLink } from '../community.ts'
import { COURSE_STEP_KEYS, COURSE_STEPS, type CourseStep } from '../day.ts'
import { cellsOfWhole, mapGrid, type Cell } from '../map.ts'
import { PRACTICE_SEED_HEX } from '../practice-seed.ts'
import { defineScreen } from '../screen.ts'
import { codeParagraphs } from './code.ts'
import type { Store } from '../store.ts'
import { errorText, paragraphs } from '../text.ts'

/**
 * The landing, and joining and returning behind it, in the same register
 * (#59, #136, Q27.2).
 *
 * A visitor who is not signed in meets the landing whatever route they came
 * by: what the Guild is, what a day in it is, what is different here, and
 * the two keys. The forms are behind the keys and each keeps a way back. A
 * signed-in player never sees any of it; `#/auth` is where they read the
 * welcome, once, after joining, and is otherwise not theirs.
 *
 * The username is the only string a player writes anywhere in the game, so it
 * is the only input in the app that is not a list of choices. Its rule is read
 * from `GAME_CONFIG` rather than restated here, and the server decides in the
 * end: this only saves a round trip and the rate-limit token that would go
 * with it.
 *
 * The welcome note is shown once, after registration, in the guild's voice;
 * under it, once, the code of the Guild with one verb, and only then the
 * practice offer (#68, Q28.2). Both are remembered here, in the session
 * store, and never on sign-in: a returning player is not welcomed again and
 * is not asked to read the code again.
 */

const { pattern } = GAME_CONFIG.account.username
/** From the contract, so the form and the API agree on what a word may be. */
const passwordMin = coreApi.passwordSchema.minLength ?? 0

type Mode = 'landing' | 'join' | 'return'

type State = {
  mode: Mode
  username: string
  password: string
  problem: string | null
  busy: boolean
  /** Set after a registration, so the welcome is read before the island. */
  welcomed: boolean
  /** Set once the code under the welcome has been read, so the offer follows it (#68). */
  codeRead: boolean
}

export const authScreen = defineScreen<State>({
  id: 'auth',
  initial: {
    mode: 'landing',
    username: '',
    password: '',
    problem: null,
    busy: false,
    welcomed: false,
    codeRead: false,
  },

  enter(app, _route, store) {
    store.set({ problem: null, busy: false })
    // `#/auth` is where the welcome is read, once, after joining. A player who
    // is already signed in and has read it has no business on this screen.
    if (app.user !== null && !store.get().welcomed) app.go({ screen: 'island', day: null })
  },

  render(state, app, store) {
    if (state.welcomed) {
      return html`<section class="screen screen-narrow">
        <h1 class="title">${app.t('authJoin')}</h1>
        <div class="welcome">
          ${paragraphs(app.content.guildVoice(app.locale).welcome).map(
            (paragraph) => html`<p>${paragraph}</p>`,
          )}
        </div>
        ${state.codeRead ? offer(app, store) : firstRead(app, store)}
      </section>`
    }

    // Not for a signed-in player (#136): `enter` sends them to the island,
    // and until the hash has moved there is nothing here to draw for them.
    if (app.user !== null) return html`<section class="screen screen-narrow"></section>`
    if (state.mode === 'landing') return landing(app, store)

    const joining = state.mode === 'join'
    const heading = app.t(joining ? 'authJoin' : 'authReturn')

    const submit = async (event: Event) => {
      event.preventDefault()
      if (state.busy) return
      if (joining && !pattern.test(state.username)) {
        store.set({ problem: app.t('authUsernameRule') })
        return
      }
      if (joining && state.password.length < passwordMin) {
        store.set({ problem: app.t('authPasswordRule', { n: passwordMin }) })
        return
      }

      store.set({ busy: true, problem: null })
      const result = joining
        ? await app.api.register(state.username, state.password)
        : await app.api.login(state.username, state.password)
      if (!result.ok) {
        store.set({ busy: false, problem: errorText(app, result) })
        return
      }
      await app.setSession(result.value.user)
      if (joining) {
        // The guild's welcome, in its own voice, before the first island.
        store.set({ busy: false, welcomed: true, password: '' })
        app.go({ screen: 'auth' })
        return
      }
      store.reset()
      app.go({ screen: 'island', day: null })
    }

    return html`<section class="screen screen-narrow">
      <h1 class="title">${heading}</h1>
      <form class="form" @submit=${submit}>
        <label class="field">
          <span class="label">${app.t('authUsername')}</span>
          <input
            name="username"
            autocomplete="username"
            .value=${state.username}
            @input=${(event: Event) =>
              store.set({ username: (event.target as HTMLInputElement).value })}
          />
        </label>
        <p class="hint">${app.t('authUsernameRule')}</p>
        <label class="field">
          <span class="label">${app.t('authPassword')}</span>
          <input
            name="password"
            type="password"
            autocomplete=${joining ? 'new-password' : 'current-password'}
            .value=${state.password}
            @input=${(event: Event) =>
              store.set({ password: (event.target as HTMLInputElement).value })}
          />
        </label>
        ${
          state.problem === null ? null : html`<p class="problem" role="alert">${state.problem}</p>`
        }
        <button class="verb verb-primary verb-end" type="submit" ?disabled=${state.busy}>
          [ ${heading} ]
        </button>
      </form>
      <p class="row">
        <button
          class="quiet"
          @click=${() => store.set({ mode: joining ? 'return' : 'join', problem: null })}
        >
          ${app.t(joining ? 'authReturn' : 'authJoin')}
        </button>
        <!-- The way back to the landing (#136). -->
        <button
          class="quiet auth-back"
          @click=${() => store.set({ mode: 'landing', problem: null })}
        >
          ${app.t('stepBack')}
        </button>
      </p>
    </section>`
  },
})

/**
 * The code of the Guild, read once, after the welcome and before the
 * practice offer (#68, Q28.2). The same paragraphs the code screen draws,
 * with one verb under them; pressing it is remembered for the session.
 */
function firstRead(app: App, store: Store<State>): TemplateResult {
  return html`<div class="first-read">
    <h2 class="label">${app.t('codeOfConductHeading')}</h2>
    <div class="code">${codeParagraphs(app)}</div>
    <p class="row">
      <button class="verb verb-primary code-agree" @click=${() => store.set({ codeRead: true })}>
        [ ${app.t('codeOfConductAgree')} ]
      </button>
    </p>
  </div>`
}

/**
 * The practice shore, offered once, here and nowhere else (#134): before
 * the first real landing, which is the only first landing that island gets.
 * Later it is on the desk.
 */
function offer(app: App, store: Store<State>): TemplateResult {
  return html`<div class="offer">
    <p class="note">${app.t('practiceOffer')}</p>
    <div class="row">
      <button
        class="verb verb-primary"
        @click=${() => {
          store.reset()
          void app.enterPractice()
        }}
      >
        [ ${app.t('practiceTake')} ]
      </button>
      <button
        class="verb"
        @click=${() => {
          store.reset()
          app.go({ screen: 'island', day: null })
        }}
      >
        [ ${app.t('practiceSkip')} ]
      </button>
    </div>
  </div>`
}

/**
 * The practice island, revealed, as the landing's picture (#136).
 *
 * Drawn once per load: the generator is deterministic and the seed is fixed,
 * so this is the same island every visitor sees and the one the practice
 * shore lands them on (#134).
 */
let picture: Cell[] | null = null
const illustration = (): Cell[] => {
  picture ??= cellsOfWhole(generateIsland(seedFromHex(PRACTICE_SEED_HEX)))
  return picture
}

/** The line under each step of the day, as the landing tells it. */
const LANDING_HOW_KEYS: Readonly<Record<CourseStep, string>> = {
  post: 'landingHowPost',
  sinking: 'landingHowSinking',
  rising: 'landingHowRising',
  letter: 'landingHowLetter',
}

/** The four things that are different here, in the order the issue lists them. */
const LANDING_DIFFERENT_KEYS = [
  'landingDifferent1',
  'landingDifferent2',
  'landingDifferent3',
  'landingDifferent4',
] as const

/**
 * The landing (#136, Q27.2): the guild's paper and voice, plain about the
 * game. The sections in the order the issue lists them, and the two keys
 * last, once the visitor knows what they are for.
 */
function landing(app: App, store: Store<State>): TemplateResult {
  return html`<section class="screen screen-landing">
    <header class="landing-head">
      <h1 class="title landing-title">${app.t('guildName')}</h1>
      <p class="landing-lead">${app.t('landingLead')}</p>
    </header>

    <div class="landing-picture">${mapGrid(illustration(), { illustration: true })}</div>

    <p class="landing-what">${app.t('landingWhat')}</p>

    <section class="landing-section">
      <h2 class="label">${app.t('landingHowHeading')}</h2>
      <ol class="landing-how">
        ${COURSE_STEPS.map(
          (step) =>
            html`<li>
              <p class="landing-step">${app.t(COURSE_STEP_KEYS[step])}</p>
              <p class="landing-line">${app.t(LANDING_HOW_KEYS[step])}</p>
            </li>`,
        )}
      </ol>
    </section>

    <section class="landing-section">
      <h2 class="label">${app.t('landingDifferentHeading')}</h2>
      <ul class="landing-different">
        ${LANDING_DIFFERENT_KEYS.map((key) => html`<li class="landing-line">${app.t(key)}</li>`)}
      </ul>
    </section>

    <footer class="landing-foot">
      <p class="landing-time">${app.t('landingTime')}</p>
      <p class="row">
        <button class="verb verb-primary" @click=${() => store.set({ mode: 'join' })}>
          [ ${app.t('authJoin')} ]
        </button>
        <button class="verb" @click=${() => store.set({ mode: 'return' })}>
          [ ${app.t('authReturn')} ]
        </button>
      </p>
      <!-- The code of the Guild, for anyone who wants to read it before joining
           (#68), and beside it the Guild's common room, with the line that
           says why the talking happens elsewhere (#138). -->
      <p class="landing-line landing-community">${app.t('communityNote')}</p>
      <p class="row">
        <button class="quiet landing-code" @click=${() => app.go({ screen: 'code' })}>
          ${app.t('navCodeOfConduct')}
        </button>
        ${communityLink(app)}
      </p>
    </footer>
  </section>`
}

export type { State as AuthState }
